import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getApiErrorMessage, request } from 'Features/auth/axios-client'
import { useState } from 'react'
import { PLAN_STATUS_FETCH_TIMEOUT } from 'Common/Constants/Constants'
import {
  EditSettingsTableDataTypes,
  RunPlanVariables,
  UploadDataProps,
} from './EditSettings.types'

import { toast } from 'react-hot-toast'

const fetchPlanStatus = async (planId: string) => {
  const data = await request({
    url: `/plan/${planId}/status`,
  })
  return data
}

export const usePlanStatus = (planId: string) => {
  const [shouldRefetch, setShouldRefetch] = useState(true)
  const { data, isLoading } = useQuery(
    ['plan-status', planId],
    () => fetchPlanStatus(planId),
    {
      select: ({ data }) => data?.data,
      onSuccess: (data) => {
        const status = data?.status
        if (status !== 'INITIATED') {
          setShouldRefetch(false)
        }
      },
      cacheTime: 0,
      refetchInterval: shouldRefetch ? PLAN_STATUS_FETCH_TIMEOUT : false,
    }
  )
  return {
    data: data,
    isLoading,
  }
}

const fetchExtractedData = async (planId: string) => {
  const data = await request({
    url: `/plan/${planId}/extracted-data`,
  })
  return data
}

export const useExtractedData = (planId: string, enabled: boolean) => {
  return useQuery(
    ['loaded-plan-data', planId],
    () => fetchExtractedData(planId),
    {
      select: ({ data }) => {
        return data?.data.map(
          (row: EditSettingsTableDataTypes, index: number) => {
            return {
              ...row,
              id: index,
            }
          }
        )
      },

      enabled: enabled,
    }
  )
}
export const initiateRunPlan = async ({
  planId,
  parameters,
}: RunPlanVariables) => {
  const { data } = await request({
    url: `/plan/${planId}/run-plan`,
    data: parameters,
    method: 'post',
  })
  return data
}

export const useRunPlan = (
  onPlanRunInitiatedSucces: () => void,
  onPlanRunInitiatedFailed: (message: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(initiateRunPlan, {
    onSuccess: async () => {
      toast.success('Plan run initiated.')
      onPlanRunInitiatedSucces()
    },
    onError: (error, _previous, context) => {
      // This hook has no onMutate, so there is usually no context: reading
      // context.previousData threw here, before the failure callback, and
      // left the mutation loading (F13).
      const previousData = (context as { previousData?: unknown } | undefined)
        ?.previousData
      if (previousData) {
        queryClient.setQueryData(['operational-plans'], previousData)
      }
      queryClient.invalidateQueries(['operational-plans'])
      onPlanRunInitiatedFailed(
        getApiErrorMessage(error, 'Plan run failed to initiate.')
      )
    },
  })
}

const bulkUpdate = async (bulkUpdateData: {
  planId: string
  data: EditSettingsTableDataTypes[]
}) => {
  const { data } = await request({
    url: `/plan/${bulkUpdateData.planId}/bulk-update`,
    method: 'post',
    data: {
      data: bulkUpdateData.data,
    },
  })
  return data
}

export const useBulkUpdate = (
  onBulkUpdateSuccess: (data: EditSettingsTableDataTypes[]) => void,
  onBulkUpdateFail: () => void
) => {
  return useMutation(bulkUpdate, {
    onSuccess: ({ data }) => {
      const idd = data.map(
        (row: EditSettingsTableDataTypes, index: number) => ({
          ...row,
          id: index,
        })
      )
      onBulkUpdateSuccess(idd)
    },
    onError: () => {
      onBulkUpdateFail()
    },
  })
}

//Upload data

const uploadData = async ({ file, planId }: UploadDataProps) => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await request({
    url: `/plan/${planId}/wc/upload`,
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const useUploadData = () => {
  return useMutation(uploadData, {
    onSuccess: () => {
      toast.success('Data uploaded successfully')
    },
    onError: () => {
      toast.error('Failed to upload data')
    },
  })
}

/************** Frozen movement */

const fetchFrozenMovementData = async (planId: string) => {
  const data = await request({
    url: `/plan/${planId}/frozen-movement-data`,
  })
  return data
}

export const useFrozenMovement = (planId: string, enabled = true) => {
  return useQuery(
    ['frozen-movement-plan-data', planId],
    () => fetchFrozenMovementData(planId),
    {
      select: ({ data }) => {
        return data?.data.map(
          (row: EditSettingsTableDataTypes, index: number) => {
            return {
              ...row,
              id: index,
            }
          }
        )
      },

      enabled: enabled,
    }
  )
}

/*************** End frozen movement */

/************** Frozen production */

const fetchFrozenProductionData = async (planId: string) => {
  const data = await request({
    url: `/plan/${planId}/frozen-production-data`,
  })
  return data
}

export const useFrozenProduction = (planId: string, enabled = true) => {
  return useQuery(
    ['frozen-production-plan-data', planId],
    () => fetchFrozenProductionData(planId),
    {
      select: ({ data }) => {
        return data?.data.map(
          (row: EditSettingsTableDataTypes, index: number) => {
            return {
              ...row,
              id: index,
            }
          }
        )
      },

      enabled: enabled,
    }
  )
}

/*************** End frozen production */
